import { NFL_TEAMS_TOTAL, TeamNamesByID } from '@shared/constants';
import { DESKTOP_VERTICAL_BAR_HEIGHT_SMALL, MOBILE_VERTICAL_BAR_HEIGHT } from 'app/constants';
import TeamVerticalChart from 'components/teamverticalchart/TeamVerticalChart';
import { calculateLengthValue } from 'helpers/ChartHelpers';
import useViewportDimensions from 'hooks/useViewportDimensions';
import React, { useCallback, useEffect, useState } from 'react';
import { TeamRankInfo } from 'types';
import styles from './Rank.module.scss';

export const DisplayType = {
  HomeTeam: styles.HomeTeam,
  Leader: styles.Leader,
  AwayTeam: styles.AwayTeam,
};

type VerticalLineProps = {
  className?: string;
  rankInfo: TeamRankInfo;
  displayType: 'HomeTeam' | 'Leader' | 'AwayTeam';
  chartBy?: 'Rank' | 'Value';
  startLabel?: React.ReactNode;
  endLabel?: React.ReactNode;
  desktopMinHeight: number;
};

const VerticalLine: React.FC<VerticalLineProps> = ({
  className = '',
  rankInfo,
  displayType,
  chartBy = 'Rank',
  startLabel,
  endLabel,
  desktopMinHeight = DESKTOP_VERTICAL_BAR_HEIGHT_SMALL,
}) => {
  const baseCls = `${styles.RankLineTeam} ${styles[TeamNamesByID[parseInt(rankInfo.teamID)]]} ${className} `;
  const { isAppMobile } = useViewportDimensions();

  const [lengthValue, setLengthValue] = useState<string>('0');

  const chartByRank = useCallback((rankInfo: TeamRankInfo) => {
    const percentage = 100 - Math.floor((rankInfo.teamRank * 100) / NFL_TEAMS_TOTAL);
    // in case value is zero, add a small % to be shown
    setLengthValue(`${Math.min(percentage + 3, 100)}`);
  }, []);

  const chartByValue = useCallback((rankInfo: TeamRankInfo) => {
    const isHi = rankInfo.highOrLow === 'High';
    const value = isHi ? rankInfo.teamValue / rankInfo.bestTeam!.value : rankInfo.teamValue / rankInfo.worstTeam!.value;

    const percentage = value * 100;
    setLengthValue(`${Math.min(percentage + 3, 100)}`);
  }, []);

  const updateChart = useCallback((chartBy: 'Rank' | 'Value', rankInfo: TeamRankInfo, isAppMobile: boolean) => {
    if (chartBy === 'Value' && rankInfo.bestTeam) {
      chartByValue(rankInfo);
    } else if (chartBy === 'Rank') {
      chartByRank(rankInfo);
    } else {
      setLengthValue(calculateLengthValue(rankInfo, chartBy, isAppMobile ? 250 : 280));
    }
  }, []);

  useEffect(() => {
    if (!rankInfo) return;

    const timerId = setTimeout(() => {
      updateChart(chartBy, rankInfo, isAppMobile);
    }, 300);

    return () => clearTimeout(timerId);
  }, [rankInfo, displayType, chartBy, updateChart, isAppMobile]);

  const getDimensionStyle = () => {
    const heightValue = isAppMobile ? `${MOBILE_VERTICAL_BAR_HEIGHT}px` : `${desktopMinHeight}px`;
    return { maxHeight: heightValue, height: heightValue, minHeight: heightValue };
  };

  return (
    <TeamVerticalChart
      className={baseCls}
      teamId={parseInt(rankInfo.teamID)}
      style={getDimensionStyle()}
      value={parseFloat(lengthValue.replace('px', ''))}
      startLabel={startLabel}
      endLabel={endLabel}
    />
  );

  // <span className={`${baseCls} ${graphCls}`} style={getDimensionStyle()}></span>;
};

export default VerticalLine;
